import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "../../assets/icons/home.svg";
import WifiIcon from "../../assets/icons/wifi.svg";
import ProfileIcon from "../../assets/icons/profile.svg";
import WalletIcon from "../../assets/icons/wallet-money.svg";
import ChartIcon from "../../assets/icons/chart.svg";
import SignpostIcon from "../../assets/icons/signpost.svg";
import ReceiptIcon from "../../assets/icons/receipt.svg";
import MembershipType from "../../assets/icons/ReceiptIcon";
import SettingsIcon from "../../assets/icons/settings.svg";
import SidebarRightIcon from "../../assets/icons/sidebar-right.svg";
import AppLogo from "../../assets/icons/logo.svg";
import DashboardIcon from "../../assets/icons/dashboard.svg";
import ContactsIcon from "../../assets/icons/contact.svg";
import OrganizationsIcon from "../../assets/icons/organization.svg";
import QuickFinderIcon from "../../assets/icons/quick-finder.svg";
import styled from "styled-components";
import { useTheme } from "../../middleware/ThemeContext";
import FormsIcon from "../../assets/icons/FormsIcon";
import DocumentIcon from "../../assets/icons/document-text";
import SearchQueryIcon from "../../assets/icons/SearchQueryIcon";
import ContactRoleIcon from "../../assets/icons/ContactRoleIcon";
import ImportIcon from "../../assets/icons/ImportIcon";
import WidgetIcon from "../../assets/icons/WidgetIcon";
import BrushIcon from "../../assets/icons/BrushIcon";
import GearSettings from "../../assets/icons/GearSettings";
import GraphIcon from "../../assets/icons/GraphIcon";
import BankingFinanceIcon from "../../assets/icons/BankingFinanceIcon";
const MenuContainer = styled("div")(({ theme }) => ({
  display: "flex",
  // zIndex: 1,
  position: "relative",
  minHeight: "100vh",
}));

const MainMenu = styled("div")<{ isCollapsed: boolean }>(
  ({ isCollapsed, theme }) => ({
    width: isCollapsed ? "50px" : "200px",
    minWidth: "50px",
    padding: "16px 0",
    backgroundColor: theme.colors.mainMenuBackground,
    color: theme.colors.menuText,
    position: "relative",
    transition: "width 0.3s ease",
    fontFamily: "Raleway, sans-serif",
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: 0,
      height: "100%",
      width: "1px",
      background: `linear-gradient(to bottom, ${theme.colors.mainMenuBackground}, ${theme.colors.white}, ${theme.colors.subMenuBackground})`,
    },
  })
);

const SubMenu = styled("div")<{ open: boolean; isCollapsed: boolean }>(
  ({ open, isCollapsed, theme }) => ({
    width: open && !isCollapsed ? "170px" : open && isCollapsed ? "200px" : "0",
    overflow: "hidden",
    transition: "width 0.3s ease",
    backgroundColor: theme.colors.subMenuBackground,
    color: theme.colors.menuText,
    position: "relative",
    padding: "16px 0",
  })
);

const MenuHeader = styled("div")<{ isCollapsed: boolean }>(
  ({ isCollapsed }) => ({
    padding: isCollapsed ? "0px 0" : "0px 12px",
    marginBottom: "16px",
  })
);

const SubMenuHeader = styled("div")({
  height: "32px",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "0px 12px",
});

const LogoContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const LogoImage = styled("img")({
  width: "32px",
  height: "32px",
});

const AppTitle = styled("div")(({ theme }) => ({
  color: theme.colors.MenuText,
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  transition: "opacity 0.3s ease, margin-left 0.3s ease",
}));

const MenuItem = styled("div")<{ active?: boolean; isCollapsed?: boolean }>(
  ({ active, isCollapsed, theme }) => ({
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: 500,
    backgroundColor:
      active && !isCollapsed ? theme.colors.selectedMenuItemBG : "transparent",
    overflow: "hidden",
    "&:hover": {
      backgroundColor: theme.colors.Overlay,
    },
  })
);
const SubMenuItem = styled("div")<{ active?: boolean }>(
  ({ active, theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 500,
    backgroundColor: active ? theme.colors.subMenuSelectedItemBG : 'transparent',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: theme.colors.Overlay,
    },
  })
);

const IconWrapperMain = styled("div")<{
  active?: boolean;
  isCollapsed?: boolean;
}>(({ active, isCollapsed, theme }) => ({
  color: theme.colors.MenuText,
  minWidth: "36px",
  minHeight: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  marginRight: "4px",
  marginLeft: "4px",
  backgroundColor:
    active && isCollapsed ? theme.colors.selectedMenuItemBG : "transparent",
  // padding: '4px 4px',
  "& img": {
    width: "20px",
    height: "20px",
    filter: "brightness(0) invert(1)",
  },
}));

const IconWrapperSub = styled("div")(({ theme }) => ({
  color: theme.colors.MenuText,
  minWidth: "18px",
  marginRight: "10px",
  "& img": {
    width: "18px",
    height: "18px",
    filter: "brightness(0) invert(1)",
  },
}));

const MenuLabel = styled("span")(({ theme }) => ({
  whiteSpace: "nowrap",
  fontWeight: 500,
  fontSize: "14px",
  color: theme.colors.MenuText,
  transition: "opacity 0.3s ease, margin-left 0.3s ease",
}));

const ToggleButton = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "20px",
  left: "16px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  color: `${theme.colors.MenuText}B3`,
  "&:hover": {
    color: theme.colors.MenuText,
  },
  "& img": {
    width: "20px",
    height: "20px",
    transition: "transform 0.3s ease",
  },
}));

interface SubMenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  path: string;
}

interface MenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  subMenu: SubMenuItem[];
  path?: string; // Optional path for main menu items
}

interface SidebarMenuProps {
  onSubMenuItemClick: (path: React.ReactNode) => void;
  isSubMenuCollapsed: boolean;
  setSubMenuCollapsed: (value: boolean) => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  isSubMenuCollapsed,
  setSubMenuCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  // Initialize from localStorage with fallback defaults
  const [selectedMainMenu, setSelectedMainMenu] = useState<string>(() => {
    return localStorage.getItem("selectedMainMenu") || "crm";
  });

  const [selectedSubMenu, setSelectedSubMenu] = useState<string>(() => {
    return localStorage.getItem("selectedSubMenu") || "contacts";
  });

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });

  // Sync state to localStorage
  useEffect(() => {
    localStorage.setItem("selectedMainMenu", selectedMainMenu);
  }, [selectedMainMenu]);

  useEffect(() => {
    localStorage.setItem("selectedSubMenu", selectedSubMenu);
  }, [selectedSubMenu]);

  const mainMenuItems: MenuItem[] = [
    {
      id: "dashboard",
      icon: <img src={HomeIcon} alt="Dashboard" />,
      label: "Dashboard",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "crm",
      icon: <img src={WifiIcon} alt="CRM" />,
      label: "CRM",
      subMenu: [
        {
          id: "contacts",
          icon: <img src={ContactsIcon} alt="" />,
          label: "Contacts",
          path: "/home/contacts",
        },
        {
          id: "organizations",
          icon: <img src={OrganizationsIcon} alt="" />,
          label: "Organizations",
          path: "/home/organizations",
        },
        {
          id: "contact-roles",
          icon: <ContactRoleIcon />,
          label: "Contact Roles",
          path: "/home/contact-roles",
        },
        {
          id: "duplicate-checker",
          icon: <SearchQueryIcon />,
          label: "Duplicate Checker",
          path: "/home/duplicate-checker",
        },
      ],
    },
    {
      id: "communities",
      icon: <img src={ProfileIcon} alt="Communities" />,
      label: "Communities",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "accounting",
      icon: <img src={WalletIcon} alt="Accounting" />,
      label: "Accounting",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "certifications",
      icon: <DocumentIcon />,
      label: "Certifications",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "reporting",
      icon: <img src={ChartIcon} alt="Reporting" />,
      label: "Analysis",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "events",
      icon: <img src={SignpostIcon} alt="Events" />,
      label: "Events",
      path: '/home/comingSoon',
      subMenu: [],
    },
    {
      id: "memberships",
      icon: <MembershipType size={20} />,
      label: "Memberships",
      // path: '/home/comingSoon',
      subMenu: [
        {id: 'dashboard', icon: <GraphIcon />, label: 'Dashboard', path: '/home/comingSoon'},
        {id: 'memberships', icon: <MembershipType />, label: 'Membership Types', path: '/home/comingSoon'},
        {id: 'applications', icon: <ContactRoleIcon />, label: 'Applications', path: '/home/comingSoon'},
        {id: 'members', icon: <ContactRoleIcon />, label: 'Members', path: '/home/comingSoon'},
        {id: 'banking-finance', icon: <BankingFinanceIcon />, label: 'Banking & Finance', path: '/home/comingSoon'},
      ],
    },
    {
      id: "forms",
      icon: <FormsIcon />,
      label: "Forms",
      subMenu: [
        { id: 'fields', icon: <FormsIcon />, label: 'Fields', path: '/home/fields' },
        { id: 'components', icon: <FormsIcon />, label: 'Components', path: '/home/components' },
        { id: 'forms', icon: <FormsIcon />, label: 'Forms', path: '/home/forms' },
        { id: 'widgets', icon: <WidgetIcon />, label: 'Widgets', path: '/home/widgets' },

      ],
    },
    {
      id: "search-query",
      icon: <SearchQueryIcon />,
      label: "Search Query",
      path: "/home/search-query",
      subMenu: [],
    },
    {
      id: "settings",
      icon: <img src={SettingsIcon} alt="Settings" />,
      label: "Settings",
      path: '/home/comingSoon',
      subMenu: [
        {
          id: "website-builder",
          icon: <BrushIcon /> ,
          label: "Website Builder",
          path: "/home/website-builder",
        },
        {
          id: "account-settings",
          icon: <GearSettings /> ,
          label: "Account Settings",
          path: "/home/comingSoon",
        },
      ],
    },
    {
      id: "import",
      icon: <ImportIcon color={theme.CorporateTheme.colors.white} />,
      label: "Import",
      subMenu: [
        { id: 'imports', icon: <FormsIcon />, label: 'Import Center', path: '/home/imports' },
      ],
    },
  ];

  // 🔁 Sync UI state with current URL (on load & route change)
  useEffect(() => {
    const { pathname } = location;

    // Check if current path matches any sub-menu item
    for (const main of mainMenuItems) {
      if (main.subMenu?.length) {
        const matchedSub = main.subMenu.find(sub => sub.path === pathname);
        if (matchedSub) {
          setSelectedMainMenu(main.id);
          setSelectedSubMenu(matchedSub.id);
          setSubMenuCollapsed(false);
          return;
        }
      } else if (main.path === pathname) {
        setSelectedMainMenu(main.id);
        setSelectedSubMenu("");
        setSubMenuCollapsed(true);
        return;
      }
    }

    // If on a default/placeholder route, redirect to default (CRM → Contacts)
    const isPlaceholder = ['/home/comingSoon', '/home', '/', ''].includes(pathname);
    if (isPlaceholder) {
      const crm = mainMenuItems.find(m => m.id === 'crm');
      if (crm?.subMenu?.[0]) {
        const first = crm.subMenu[0];
        setSelectedMainMenu('crm');
        setSelectedSubMenu('contacts');
        navigate(first.path);
        setSubMenuCollapsed(false);
      }
    }
  }, [location.pathname]);

  // 🖱️ Handle main menu click
  const handleMainMenuClick = (menuId: string, path?: string) => {
    const mainMenu = mainMenuItems.find(m => m.id === menuId);
    setSelectedMainMenu(menuId);

    if (mainMenu?.subMenu && mainMenu.subMenu.length > 0) {
      // Has sub-menu → go to first child
      const firstSub = mainMenu.subMenu[0];
      setSelectedSubMenu(firstSub.id);
      navigate(firstSub.path);
      setSubMenuCollapsed(false);
    } else if (path) {
      // No sub-menu → go directly
      setSelectedSubMenu("");
      navigate(path);
      setSubMenuCollapsed(true);
    }

    // Initialize sidebar collapse if not set
    if (isCollapsed === null) {
      setIsCollapsed(true);
      localStorage.setItem("sidebarCollapsed", "true");
    }
  };

  // 🔄 Toggle sidebar collapse
  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  // 🔍 Determine if current main menu has sub-menu
  const currentMainMenu = mainMenuItems.find(m => m.id === selectedMainMenu);
  const hasSubMenu = Boolean(currentMainMenu?.subMenu?.length);

  return (
    <MenuContainer theme={theme}>
      <MainMenu theme={theme} isCollapsed={isCollapsed}>
        <MenuHeader isCollapsed={isCollapsed}>
          <LogoContainer>
            <LogoImage
              src={AppLogo}
              alt="App Logo"
              style={{
                marginLeft: isCollapsed ? "8px" : "0",
                transition: "margin 0.3s ease",
              }}
            />
            <AppTitle
              theme={theme}
              style={{
                opacity: isCollapsed ? 0 : 1,
                marginLeft: isCollapsed ? "-100%" : "0",
              }}
            >
              MEMBER THINGS
            </AppTitle>
          </LogoContainer>
        </MenuHeader>

        {mainMenuItems.map((menu) => (
          <MenuItem
            theme={theme}
            key={menu.id}
            active={selectedMainMenu === menu.id}
            isCollapsed={isCollapsed}
            onClick={() => handleMainMenuClick(menu.id, menu.path)}
          >
            <IconWrapperMain
              active={selectedMainMenu === menu.id}
              isCollapsed={isCollapsed}
              theme={theme}
            >
              {menu.icon}
            </IconWrapperMain>
            <MenuLabel
              theme={theme}
              style={{
                opacity: isCollapsed ? 0 : 1,
                marginLeft: isCollapsed ? "-100%" : "0",
              }}
            >
              {menu.label}
            </MenuLabel>
          </MenuItem>
        ))}

        <ToggleButton theme={theme} onClick={handleToggleCollapse}>
          <img
            src={SidebarRightIcon}
            alt="Toggle sidebar"
            style={{
              transform: isCollapsed ? "rotate(0deg)" : "rotate(180deg)",
            }}
          />
        </ToggleButton>
      </MainMenu>

      {/* SubMenu only opens if current main menu HAS sub-items */}
      <SubMenu
        open={hasSubMenu && !isSubMenuCollapsed}
        isCollapsed={isCollapsed}
        theme={theme}
      >
        <SubMenuHeader>
          <div
            style={{
              color: "#FFFFFF",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentMainMenu?.label}
          </div>
        </SubMenuHeader>
        {currentMainMenu?.subMenu.map((item) => (
          <SubMenuItem
            theme={theme}
            key={item.id}
            active={selectedSubMenu === item.id}
            onClick={() => {
              navigate(item.path);
              setSelectedSubMenu(item.id);
              setSubMenuCollapsed(false);
            }}
          >
            <IconWrapperSub theme={theme}>{item.icon}</IconWrapperSub>
            <MenuLabel theme={theme}>{item.label}</MenuLabel>
          </SubMenuItem>
        ))}
      </SubMenu>
    </MenuContainer>
  );
};

export default SidebarMenu;


{
  /* <SubMenuItem theme={theme} key={item.id}
active={selectedSubMenu === item.id}
onClick={() => {
    onSubMenuItemClick(item.path);
    handleSubMenuClick(item.id);
}}>
<IconWrapperSub theme={theme}>{item.icon}</IconWrapperSub>
<MenuLabel theme={theme}>{item.label}</MenuLabel>
</SubMenuItem> */
}
